import { COOKIES } from '@bulknutrients/bulk-shared-lib/constants';
import { datadogLogs } from '@datadog/browser-logs';
import type { LogOptions } from '@lib/logs';
import {
  addBreadcrumb,
  type Breadcrumb,
  captureException,
  captureMessage,
} from '@sentry/nextjs';
import Cookies from 'js-cookie';

datadogLogs.init({
  clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
  site: 'ap2.datadoghq.com',
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
});

const LOCAL_LOG = false;

/**
 * Send to sentry and our logs if on server
 * @param breadcrumb Sentry Breadcrumb
 */
export const logBreadcrumb = (breadcrumb: Breadcrumb) => {
  addBreadcrumb(breadcrumb); // send to sentry
  datadogLogs.logger.info('Breadcrumb', breadcrumb); // send to DataDog

  if (process.env.NEXT_PUBLIC_SERVER_URL?.includes('localhost') && LOCAL_LOG)
    console.log(breadcrumb.message, breadcrumb); // log it if localhost
};

/**
 * Used to send errors and messages to our internal logs and sentry
 * @param message either a string or an error
 */
export const log = ({ error, message, location }: LogOptions) => {
  const detail = error ?? message; // so we can use either error or message and it make sense

  // Add a breadcrumb to sentry of the session id
  addBreadcrumb({
    message: 'Location and Session',
    data: {
      location,
      session: Cookies.get(COOKIES.session),
      user_uuid: Cookies.get(COOKIES.tracking),
    },
  });

  // Actual error type
  if (detail instanceof Error) {
    captureException(detail); // send to sentry
    datadogLogs.logger.error(
      detail.message,
      {
        location,
        session: Cookies.get(COOKIES.session),
        user_uuid: Cookies.get(COOKIES.tracking),
      },
      detail
    ); // send to DataDog

    // print to console if localhost
    if (process.env.NEXT_PUBLIC_SERVER_URL?.includes('localhost'))
      console.error(
        `Encountered error, triggered by: ${location}. error: ${detail.message}`
      );
  } else {
    // Just a message
    const formattedDetails =
      typeof detail === 'string' ? detail : JSON.stringify(detail);

    captureMessage(formattedDetails); // send to sentry
    datadogLogs.logger.info(formattedDetails, {
      location,
      session: Cookies.get(COOKIES.session),
      user_uuid: Cookies.get(COOKIES.tracking),
    }); // send to DataDog

    if (process.env.NEXT_PUBLIC_SERVER_URL?.includes('localhost') && LOCAL_LOG)
      console.log(formattedDetails); // send to our logs
  }
};
